<template>
  <div>
    <slot v-bind="{ days, hours, minutes, seconds, milliseconds, totalDays, totalHours, totalMinutes, totalSeconds, totalMilliseconds }">
      {{ days }} days, {{ hours }} hours, {{ minutes }} minutes, {{ seconds }} seconds.
    </slot>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
  time: {
    type: Number,
    default: 0,
  },
  interval: {
    type: Number,
    default: 1000,
  },
  tag: {
    type: String,
    default: 'span',
  },
  autoStart: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['end']);

const now = ref(Date.now());
const endTime = ref(0);
const counting = ref(false);
let rafId = null;

const remaining = computed(() => Math.max(0, endTime.value - now.value));

const days = computed(() => Math.floor(remaining.value / (1000 * 60 * 60 * 24)));
const hours = computed(() => Math.floor((remaining.value % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
const minutes = computed(() => Math.floor((remaining.value % (1000 * 60 * 60)) / (1000 * 60)));
const seconds = computed(() => Math.floor((remaining.value % (1000 * 60)) / 1000));
const milliseconds = computed(() => Math.floor(remaining.value % 1000));

const totalDays = computed(() => days.value);
const totalHours = computed(() => Math.floor(remaining.value / (1000 * 60 * 60)));
const totalMinutes = computed(() => Math.floor(remaining.value / (1000 * 60)));
const totalSeconds = computed(() => Math.floor(remaining.value / 1000));
const totalMilliseconds = computed(() => remaining.value);

const stop = () => {
  counting.value = false;
  if (rafId) {
    clearTimeout(rafId);
    rafId = null;
  }
};

const tick = () => {
  if (!counting.value) return;
  
  now.value = Date.now();
  
  if (remaining.value > 0) {
    rafId = setTimeout(() => {
        requestAnimationFrame(tick);
    }, props.interval);
  } else {
    stop();
    emit('end');
  }
};

const start = () => {
  if (counting.value) return;
  counting.value = true;
  tick();
};

watch(() => props.time, (newTime) => {
  if (newTime > 0) {
    endTime.value = Date.now() + newTime;
    if (props.autoStart) {
      start();
    }
  } else {
    endTime.value = Date.now();
    stop();
  }
}, { immediate: true });

onBeforeUnmount(() => {
  stop();
});

defineExpose({ start, stop });
</script>
